import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useProgressStore } from './progressStore';
import { getProjectTrackById } from '../engine/project-tracker';

export interface ProjectProgress {
  childId: string;
  trackId: string;
  currentDay: number;
  startedAt: string;
  completedDays: number[];
  completedAt: string | null;
}

/**
 * One-shot migration that walks every saved project and writes a
 * CompletionRecord for each completed day (and a capstone record when the
 * project is fully done) into progressStore.
 *
 * Idempotent: each entry uses a deterministic activityId of
 * `project:{trackId}:day-{n}` (or `:complete`), so repeat runs don't
 * double-write.
 *
 * Needed because `completeDay` only began mirroring into progressStore
 * in a recent change, leaving historical project work invisible to the
 * parent activity feed.
 */
export function backfillProjectCompletions(): void {
  const projectState = useProjectStore.getState();
  const progressState = useProgressStore.getState();
  const existingIds = new Set(progressState.completions.map(c => c.activityId));

  for (const p of projectState.projects) {
    const track = getProjectTrackById(p.trackId);
    if (!track) continue;

    // We don't have per-day completion timestamps. Best-effort date:
    // completedAt for finished projects, else startedAt.
    const fallbackDate = (p.completedAt ?? p.startedAt).slice(0, 10);

    for (const dayNumber of p.completedDays) {
      const activityId = `project:${p.trackId}:day-${dayNumber}`;
      if (existingIds.has(activityId)) continue;
      const day = track.dailyActivities.find(d => d.dayNumber === dayNumber);
      progressState.addCompletion({
        childId: p.childId,
        activityId,
        scheduleId: 'project',
        date: fallbackDate,
        subject: day?.subject ?? track.subjects[0] ?? 'creative',
        durationMinutes: 30,
        starsEarned: 3,
        xpEarned: 50,
        themeId: 'project',
      });
      existingIds.add(activityId);
    }

    if (p.completedAt) {
      const activityId = `project:${p.trackId}:complete`;
      if (!existingIds.has(activityId)) {
        progressState.addCompletion({
          childId: p.childId,
          activityId,
          scheduleId: 'project',
          date: p.completedAt.slice(0, 10),
          subject: track.subjects[0] ?? 'creative',
          durationMinutes: 0,
          starsEarned: 3,
          xpEarned: 200,
          themeId: 'project',
        });
        existingIds.add(activityId);
      }
    }
  }
}

interface ProjectState {
  projects: ProjectProgress[];
  startProject: (childId: string, trackId: string) => void;
  completeDay: (childId: string, trackId: string, dayNumber: number) => void;
  getActiveProjects: (childId: string) => ProjectProgress[];
  getCompletedProjects: (childId: string) => ProjectProgress[];
  getProjectProgress: (childId: string, trackId: string) => ProjectProgress | undefined;
}

export const useProjectStore = create<ProjectState>()(
  persist(
    (set, get) => ({
      projects: [],

      startProject: (childId, trackId) => {
        const existing = get().projects.find(
          p => p.childId === childId && p.trackId === trackId
        );
        if (existing) return;

        const progress: ProjectProgress = {
          childId,
          trackId,
          currentDay: 1,
          startedAt: new Date().toISOString(),
          completedDays: [],
          completedAt: null,
        };
        set(state => ({
          projects: [...state.projects, progress],
        }));
      },

      completeDay: (childId, trackId, dayNumber) => {
        set(state => ({
          projects: state.projects.map(p => {
            if (p.childId !== childId || p.trackId !== trackId) return p;
            if (p.completedDays.includes(dayNumber)) return p;

            const completedDays = [...p.completedDays, dayNumber].sort((a, b) => a - b);
            const nextDay = dayNumber + 1;

            return {
              ...p,
              completedDays,
              currentDay: nextDay > p.currentDay ? nextDay : p.currentDay,
            };
          }),
        }));
      },

      getActiveProjects: (childId) => {
        return get().projects.filter(
          p => p.childId === childId && p.completedAt === null
        );
      },

      getCompletedProjects: (childId) => {
        return get().projects.filter(
          p => p.childId === childId && p.completedAt !== null
        );
      },

      getProjectProgress: (childId, trackId) => {
        return get().projects.find(
          p => p.childId === childId && p.trackId === trackId
        );
      },
    }),
    { name: 'homeschool-projects', version: 0, migrate: (state) => state }
  )
);
